import axios from "axios";
import {
  PriceListSubscriptionSearchRequest,
  PriceListSubscriptionSearchResponse,
  PriceListManufacturersResponse,
  PriceListDistributorsResponse,
  PriceListSubscription,
  PriceListSubscriptionResponse,
  PriceListResponse,
  SubscriptionClientResponse,
  PriceListSubscriptionCartResponse,
  PriceListSubscriptionDetail,
  SubscriptionResponse,
  PriceListSubscriptionCart
} from "types/subscription";
import { getFormattedCurrentDateTime } from "utils/format-date";

const PL2Url = process.env.REACT_APP_PL20API_URL || "";
const PL2Code = process.env.REACT_APP_PL20API_CODE || "";

// --------------------------------------------------------------------------------------------- //

export const PriceListSubscriptionAPI = {
  // ------------------------------------------------------------
  // AddApplicationLogEntry (temporary)
  // ------------------------------------------------------------
  addApplicationLogEntry: async (logEntry: string, logLevel: string, user: string) => {
    try {
      let url = "";

      url = `${PL2Url}/applicationlog${PL2Code}`;

      const request = {
        LogMessage: logEntry,
        Level: logLevel,
        LogDate: getFormattedCurrentDateTime(),
        LoggedBy: user
      };
      axios.post(url, request);
    } catch (error) {
      console.error(error);
    }
  },
  // ------------------------------------------------------------
  // GetPriceListSubscriptionClientByDealerId
  // ------------------------------------------------------------
  getSubscriptionClientByDealerId: async (dealerId: number) => {
    const url = `${PL2Url}/PriceListSubscriptionClient/${dealerId}${PL2Code}`;
    const { data } = await axios.get<SubscriptionClientResponse>(url);
    return data;
  },
  // ------------------------------------------------------------
  // ActivateSubscription
  //   This happens only in pre-activation phase.
  // ------------------------------------------------------------
  activateSubscription: async (subscriptionId: number, activatedBy: string) => {
    const url = `${PL2Url}/activate/${subscriptionId}/${activatedBy}${PL2Code}`;
    const { data } = await axios.put<SubscriptionResponse>(url, {});
    return data;
  },
  // ------------------------------------------------------------
  // PriceListCheckOut
  // ------------------------------------------------------------
  priceListCheckOut: async (subscriptionId: number, checkOutUser: string) => {
    const url = `${PL2Url}/pricelistsubscriptioncart/pricelistcheckout/${subscriptionId}/${checkOutUser}${PL2Code}`;
    const { data } = await axios.post<SubscriptionResponse>(url, {});
    return data;
  },
  // ------------------------------------------------------------
  // GetPriceListSubscription
  // ------------------------------------------------------------
  getPriceListSubscription: async (priceListSubscriptionId: number) => {
    const url = `${PL2Url}/PriceListSubscription/${priceListSubscriptionId}${PL2Code}`;
    const { data } = await axios.get<PriceListSubscriptionResponse>(url, {});
    return data;
  },
  // ------------------------------------------------------------
  // GetPriceListSubscriptions
  // ------------------------------------------------------------
  getPriceListSubscriptions: async (
    dmsCompany: string,
    dealerId: number,
    searchString: string,
    sortField: string,
    ascending: boolean,
    activeStatus: string,
    updateType: string,
    manufacturerList: number[],
    distributorList: number[],
    frequencyId: number,
    pageNumber: number,
    pageSize: number
  ) => {
    const url = `${PL2Url}/PriceListSubscription/Search${PL2Code}`;

    const request: PriceListSubscriptionSearchRequest = {
      searchString: searchString,
      dmsCompany: dmsCompany,
      dealerId: dealerId,
      sortField: sortField,
      ascending: ascending,
      activeStatus: activeStatus,
      updateType: updateType,
      manufacturers: manufacturerList,
      distributors: distributorList,
      frequencyId: frequencyId,
      showOnlyUpdateAvailable: false,
      priceListKey: "00000000-0000-0000-0000-000000000000",
      pageNumber: pageNumber,
      pageSize: pageSize
    };
    const { data } = await axios.post<PriceListSubscriptionSearchResponse>(url, request);
    return data;
  },
  // ------------------------------------------------------------
  // GetPriceListManufacturers
  // ------------------------------------------------------------
  getPriceListManufacturers: async (searchString: string): Promise<PriceListManufacturersResponse> => {
    const url = `${PL2Url}/PriceListManufacturers/${searchString}${PL2Code}`;
    const { data } = await axios.get<PriceListManufacturersResponse>(url);
    return data;
  },
  // ------------------------------------------------------------
  // GetPriceListManufacturersBySubscriptionId
  // ------------------------------------------------------------
  getPriceListManufacturersBySubscription: async (subscriptionId: number): Promise<PriceListManufacturersResponse> => {
    const url = `${PL2Url}/PriceListManufacturers/Subscription/${subscriptionId}${PL2Code}`;
    const { data } = await axios.get<PriceListManufacturersResponse>(url);
    return data;
  },
  // ------------------------------------------------------------
  // GetPriceListTopManufacturers
  // ------------------------------------------------------------
  getPriceListTopManufacturers: async (
    dealerId: number,
    salesMinimum: number
  ): Promise<PriceListManufacturersResponse> => {
    const url = `${PL2Url}/PriceListManufacturers/TopManufacturers/${dealerId}/${salesMinimum}${PL2Code}`;
    const { data } = await axios.get<PriceListManufacturersResponse>(url);
    return data;
  },
  // ------------------------------------------------------------
  // GetPriceListDistributors
  // ------------------------------------------------------------
  getPriceListDistributors: async (searchString: string): Promise<PriceListDistributorsResponse> => {
    const url = `${PL2Url}/PriceListDistributors/${searchString}${PL2Code}`;
    const { data } = await axios.get<PriceListDistributorsResponse>(url);
    return data;
  },
  // ------------------------------------------------------------
  // GetPriceListDistributorsBySubscriptionId
  // ------------------------------------------------------------
  getPriceListDistributorsBySubscription: async (subscriptionId: number): Promise<PriceListDistributorsResponse> => {
    const url = `${PL2Url}/PriceListDistributors/Subscription/${subscriptionId}${PL2Code}`;
    const { data } = await axios.get<PriceListDistributorsResponse>(url);
    return data;
  },
  // -------------------------------------------------------------
  // UpdatePriceListSubscription
  // -------------------------------------------------------------
  updatePriceListSubscription: async (
    priceListSubscription: PriceListSubscription
  ): Promise<PriceListSubscriptionResponse> => {
    const url = `${PL2Url}/pricelistsubscription${PL2Code}`;
    const { data } = await axios.put<PriceListSubscriptionResponse>(url, priceListSubscription);
    return data;
  },
  // -------------------------------------------------------------
  // GetAvailablePriceLists
  // -------------------------------------------------------------
  getAvailablePriceLists: async (
    dmsCompany: string,
    dealerid: number,
    manufacturers: number[],
    distributors: number[],
    searchString: string,
    sortField: string,
    ascending: boolean,
    pageNumber: number,
    pageSize: number
  ): Promise<PriceListResponse> => {
    const url = `${PL2Url}/pricelist/available${PL2Code}`;

    const request = {
      DmsCompany: dmsCompany,
      Dealerid: dealerid,
      Manufacturers: manufacturers,
      distributors: distributors,
      SearchString: searchString,
      SortField: sortField,
      Ascending: ascending,
      PageNumber: pageNumber,
      PageSize: pageSize
    };
    const { data } = await axios.post<PriceListResponse>(url, request);
    return data;
  },
  // -------------------------------------------------------------
  // AddPriceListSubscriptions - PRE-ACTIVATION
  //   Adds price list keys directly to the pricelistsubsciption table
  // -------------------------------------------------------------
  addPriceListSubscriptions: async (details: PriceListSubscriptionDetail[]): Promise<PriceListSubscriptionResponse> => {
    const url = `${PL2Url}/pricelistsubscriptions${PL2Code}`;
    const { data } = await axios.post<PriceListSubscriptionResponse>(url, details);
    return data;
  },
  // -------------------------------------------------------------
  // NewDealerSubmission
  //   The dealer has added subscirptions and submitted
  //   This api will set the new dealer status to pending
  // -------------------------------------------------------------
  newDealerSubmission: async (subscriptionId: number, submittedBy: string): Promise<SubscriptionResponse> => {
    const url = `${PL2Url}/subscription/newdealersubmission${PL2Code}`;
    const request = {
      subscriptionId: subscriptionId,
      submittedBy: submittedBy
    };
    const { data } = await axios.post<SubscriptionResponse>(url, request);
    return data;
  },
  // -------------------------------------------------------------
  // AddPriceListSubscriptionsToCart - POST ACTIVATION
  //    Adds price list keys to the cart
  // -------------------------------------------------------------
  addPriceListSubscriptionsToCart: async (
    details: PriceListSubscriptionDetail[]
  ): Promise<PriceListSubscriptionResponse> => {
    const url = `${PL2Url}/pricelistsubscriptioncart/items${PL2Code}`;
    const { data } = await axios.post<PriceListSubscriptionResponse>(url, details);
    return data;
  },
  // -------------------------------------------------------------
  // UpdatePriceListSubscriptionCart
  //   Updates a cart item
  // -------------------------------------------------------------
  updatePriceListSubscriptionCart: async (
    cartItem: PriceListSubscriptionCart
  ): Promise<PriceListSubscriptionCartResponse> => {
    const url = `${PL2Url}/pricelistsubscriptioncart${PL2Code}`;
    const { data } = await axios.put<PriceListSubscriptionCartResponse>(url, cartItem);
    return data;
  },
  // ------------------------------------------------------------
  // GetPriceList
  //   This is used when subscribing to a price list
  // ------------------------------------------------------------
  getPriceList: async (priceListKey: string): Promise<PriceListResponse> => {
    const url = `${PL2Url}/pricelistdetail/${priceListKey}${PL2Code}`;
    const { data } = await axios.get<PriceListResponse>(url);
    return data;
  },
  // -------------------------------------------------------------
  // GetPriceListSubscriptionCart
  // -------------------------------------------------------------
  getPriceListSubscriptionCart: async (priceListSubscriptionId: number): Promise<PriceListSubscriptionCartResponse> => {
    const url = `${PL2Url}/pricelistsubscriptioncart/${priceListSubscriptionId}${PL2Code}`;
    const { data } = await axios.get<PriceListSubscriptionCartResponse>(url);
    return data;
  },
  // -------------------------------------------------------------
  // UnsubscribePriceListSubscription
  // -------------------------------------------------------------
  unsubscribePriceListSubscription: async (
    subscriptionId: number,
    priceListKey: string,
    userName: string
  ): Promise<PriceListSubscriptionResponse> => {
    const url = `${PL2Url}/PriceListSubscription/PreActivation/Unsubscribe/${subscriptionId}/${priceListKey}/${userName}${PL2Code}`;
    const { data } = await axios.post<PriceListSubscriptionResponse>(url, {});
    return data;
  },
  // -------------------------------------------------------------
  // DeletePriceListSubscriptionCartById
  // -------------------------------------------------------------
  deletePriceListSubscriptionCartItemById: async (
    priceListSubscriptionCartId: number
  ): Promise<PriceListSubscriptionCartResponse> => {
    const url = `${PL2Url}/pricelistsubscriptioncart/${priceListSubscriptionCartId}${PL2Code}`;
    const { data } = await axios.delete<PriceListSubscriptionCartResponse>(url);
    return data;
  },
  // -------------------------------------------------------------
  // DeletePriceListSubscriptionCartByPriceListKey
  // -------------------------------------------------------------
  deletePriceListSubscriptionCartItemByPriceListKey: async (
    subscriptionId: number,
    priceListKey: string
  ): Promise<PriceListSubscriptionCartResponse> => {
    const url = `${PL2Url}/pricelistsubscriptioncart/${subscriptionId}/${priceListKey}${PL2Code}`;
    const { data } = await axios.delete<PriceListSubscriptionCartResponse>(url);
    return data;
  },
  // -------------------------------------------------------------
  // Pre-ActivationLoadPriceListSubscriptionCart
  // -------------------------------------------------------------
  preActivationLoadPriceListSubscriptionCart: async (
    priceListSubscriptionId: number,
    createdBy: string
  ): Promise<PriceListSubscriptionResponse> => {
    const url = `${PL2Url}/pricelistsubscriptioncart/preactivation/loadcart${PL2Code}`;
    const request = {
      PriceListSubscriptionId: priceListSubscriptionId,
      CreatedBy: createdBy,
      CreatedOn: getFormattedCurrentDateTime()
    };
    const { data } = await axios.post<PriceListSubscriptionResponse>(url, request);
    return data;
  }
};

export default PriceListSubscriptionAPI;
